// Tiny typed fetch client for the Hono API. Everything is a plain JSON
// request/response except the SSE-stream build-deck endpoint, which
// exposes its own helper (streamBuildDeck).

import type { AgentEvent, ExemplarMode } from "./types";
import { fetchAuthToken } from "./auth-token";
import type { PlanId } from "./credits";

const API_BASE = ""; // same origin — Vite proxies /api in dev

async function authHeaders(): Promise<Record<string, string>> {
  const token = await fetchAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export interface CardSummary {
  oracle_id: string;
  name: string;
  mana_cost: string | null;
  cmc: number;
  type_line: string;
  oracle_text: string;
  colors: string[];
  color_identity: string[];
  set: string;
  set_code: string;
  collector_number: string;
  rarity: string;
  price_usd: number | null;
  score: number;
  source: string;
}

export interface SearchResponse {
  query: string;
  count: number;
  hits: CardSummary[];
}

export interface DeckEntry {
  count: number;
  name: string;
  set?: string;
  collector_number?: string;
}

export interface Deck {
  format: "standard" | "pioneer";
  mainboard: DeckEntry[];
  sideboard: DeckEntry[];
}

/** Agent-generated deck identity summary. Mirrors src/bedrock/summarize-deck.ts.
 *  Only populated for meta_reference archetype decks today; user decks
 *  carry it as undefined. UI hides the summary block when undefined. */
export interface DeckAgentSummary {
  archetype_guess: string;
  curve_summary: string;
  win_condition: string;
  key_creatures: string[];
  key_spells: string[];
  sideboard_themes: string[];
}

export interface SavedDeckSummary {
  id: string;
  theme: string;
  brief: string;
  model: string;
  created_at: string;
  elapsed_ms: number;
  prompt_version?: string;
  /** Cached proximity score. Missing when a deck was saved before
   *  auto-scoring landed and hasn't been scored via the Score button. */
  proximity_score?: number;
  /** Sub-dimension breakdown of proximity_score (jaccard / core_cards /
   *  mana_base / scored_at). Missing on decks scored before the
   *  breakdown shipped — UI hides the breakdown row in that case. */
  proximity_breakdown?: {
    jaccard: number;
    core_cards: number;
    mana_base: number;
    scored_at: string;
  };
  /** Estimated Bedrock cost for this build in USD. Missing on decks
   *  saved before cost tracking shipped. */
  cost_estimate_usd?: number;
  /** Sum of card prices across mainboard + sideboard at the latest
   *  DuckDB Scryfall snapshot. Undefined if the deck didn't parse. */
  card_cost_usd?: number;
  /** Copies of cards missing price data (floor-hint for card_cost_usd). */
  card_cost_missing?: number;
  /** Set when the deck has been manually edited since save. */
  updated_at?: string;
  /** True when the deck is published for cross-user discovery, OR when
   *  it's a seeded meta_reference archetype. */
  is_public: boolean;
  /** Clerk username of the deck's owner. Used by the UI to detect
   *  "viewing someone else's deck" and switch to read-only mode. */
  owner_username: string;
  /** "user" = a normal user-built deck; "meta_reference" = a curated
   *  gauntlet archetype. UI badges them differently. */
  role: "user" | "meta_reference";
  /** Optional agent-generated identity summary. Only meta_reference
   *  decks have this populated today. */
  agent_summary?: DeckAgentSummary;
  /** Current version number on the deck's edit timeline. Equals
   *  MAX(version_no) of its deck_versions rows. Surfaced on the Saved
   *  Decks page so users can see at a glance which decks they've
   *  iterated on. Undefined only for very old decks predating the
   *  v1 backfill — defensive fallback. */
  version_no?: number;
}

export interface SavedDeckDetail extends SavedDeckSummary {
  /** Original collection used to build the deck; independent of suggestion scope. */
  collection_id?: string;
  answer: string;
  deck: Deck | null;
}

export interface PublicDeckMatch {
  owner_username: string;
  slug: string;
  theme: string;
  is_public: boolean;
  role: "user" | "meta_reference";
  updated_at: string | null;
  created_at: string;
}

export interface ProximityDimensions {
  jaccard: number;
  core_cards: number;
  mana_base: number;
}
export interface NearestMatch {
  exemplar_id: string;
  source: string;
  source_url: string;
  deck_name: string;
  author: string | null;
  tier_weight: number;
  dimensions: ProximityDimensions;
  weighted: number;
  tier_scaled: number;
}
export interface ProximityScore {
  score: number;
  /** Tier-weighted mean of jaccard/core_cards/mana_base across the
   *  same top-K used to build `score`. Surfaced so the UI can render
   *  the breakdown without re-fetching per-exemplar dimensions. */
  dimensions: ProximityDimensions;
  nearest: NearestMatch[];
  n_candidates: number;
  weights: { jaccard: number; core_cards: number; mana_base: number };
  topK: number;
}

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const auth = await authHeaders();
  const merged: RequestInit = {
    ...init,
    headers: { ...(init?.headers as Record<string, string> | undefined), ...auth },
  };
  const res = await fetch(url, merged);
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const j = await res.json();
      if (j?.error) msg = j.error;
    } catch {
      // ignore
    }
    // Attach the HTTP status to the error so callers can branch on it
    // without parsing the message string. Server error envelopes
    // (`{ error: "..." }`) replace the status text in `msg`, which
    // would otherwise hide the 404 from getDeckOrPublic's fallback.
    const err = new Error(msg) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  return (await res.json()) as T;
}

export async function searchCards(
  q: string,
  topK = 20,
): Promise<SearchResponse> {
  const url = `${API_BASE}/api/search?q=${encodeURIComponent(q)}&top_k=${topK}`;
  return jsonFetch<SearchResponse>(url);
}

export async function getCard(name: string): Promise<CardSummary & { [k: string]: unknown }> {
  const url = `${API_BASE}/api/card/${encodeURIComponent(name)}`;
  return jsonFetch(url);
}

export interface CardMeta {
  name: string;
  oracle_text?: string;
  type_line: string;
  types: string[];
  cmc: number;
  colors: string[];
  mana_cost: string | null;
  price_usd: number | null;
  set_code: string;
  scryfall_id: string;
  // Self-hosted image URLs (our bucket). image_back is non-null only for
  // double-faced cards and drives the click-to-flip viewer.
  image_normal: string | null;
  image_art_crop: string | null;
  image_back: string | null;
}

/**
 * Batch-lookup minimal metadata for N card names. Returns a map keyed by
 * lowercase name (so callers don't have to worry about case). Missing
 * cards are simply absent from the map.
 */
export async function getCardMetaBatch(
  names: string[],
): Promise<Record<string, CardMeta>> {
  if (names.length === 0) return {};
  const res = await jsonFetch<{ cards: Record<string, CardMeta> }>(
    `${API_BASE}/api/card/batch`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ names }),
    },
  );
  return res.cards;
}

export async function listDecks(
  q?: string,
): Promise<{ count: number; decks: SavedDeckSummary[] }> {
  const trimmed = q?.trim();
  const url = trimmed
    ? `${API_BASE}/api/decks?q=${encodeURIComponent(trimmed)}`
    : `${API_BASE}/api/decks`;
  return jsonFetch(url);
}

/** Raised when the server rejects an uploaded deck with a list of
 *  Standard-legality errors. Carries the `errors` array so the page
 *  can render each issue inline rather than just a one-line message. */
export class DeckUploadValidationError extends Error {
  readonly errors: string[];
  constructor(errors: string[]) {
    super(errors[0] ?? "deck failed validation");
    this.name = "DeckUploadValidationError";
    this.errors = errors;
  }
}

export interface UploadDeckDoneEvent {
  saved_id: string | null;
  answer: string;
  deck: Deck;
  theme: string;
  brief: string;
  elapsed_ms: number;
  model: string;
  prompt_version?: string;
  cost_estimate_usd?: number;
  token_usage?: Record<string, { input_tokens: number; output_tokens: number }>;
  event_count: number;
}

export type UploadDeckStreamMessage =
  | {
      kind: "meta";
      data: { theme: string; brief: string; started_at: string; model: string };
    }
  | { kind: "event"; data: AgentEvent }
  | { kind: "save_error"; data: { error: string } }
  | { kind: "done"; data: UploadDeckDoneEvent }
  | { kind: "error"; data: { error: string } };

/**
 * Stream an upload-deck agent run. The server pre-validates the deck and
 * pre-charges credits before opening the stream, so a non-OK HTTP response
 * carries one of:
 *   - 400 { error }                 — missing theme / decklist
 *   - 402 InsufficientCreditsError  — caller should trigger upgrade flow
 *   - 422 { error } | { errors[] }  — parse failure / Standard legality
 * Once the stream opens, the normal SSE events apply.
 */
/**
 * Body shape for streamUploadDeck. Most fields are optional on the server:
 *   - `mode` defaults to "standard" — set "limited" when uploading a draft
 *     or sealed pool from the /draft page.
 *   - `decklist` may be empty if `images` is non-empty; the server's vision
 *     pre-pass transcribes attached photos into Arena format.
 *   - `images` are JPEG/PNG/WebP/GIF, raw base64 (no `data:` prefix), max
 *     4 images at 2MB each — same caps the /api/builds route enforces.
 *   - `model_preference` is honored only in Limited mode (Standard upload
 *     always uses Opus). Non-subscribers asking for Opus are silently
 *     downgraded server-side.
 */
export interface StreamUploadDeckBody {
  theme: string;
  brief: string;
  decklist: string;
  mode?: "standard" | "limited";
  images?: Array<{ media_type: string; data_b64: string }>;
  model_preference?: "sonnet" | "opus";
}

export async function streamUploadDeck(
  body: StreamUploadDeckBody,
  onMessage: (msg: UploadDeckStreamMessage) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/upload-deck`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...(await authHeaders()),
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!res.ok || !res.body) {
    let payload: unknown = null;
    try { payload = await res.json(); } catch { /* non-JSON body */ }
    if (
      res.status === 422 &&
      payload &&
      typeof payload === "object" &&
      Array.isArray((payload as { errors?: unknown }).errors)
    ) {
      throw new DeckUploadValidationError(
        ((payload as { errors: unknown[] }).errors).map(String),
      );
    }
    const msg =
      payload && typeof payload === "object" && typeof (payload as { error?: unknown }).error === "string"
        ? (payload as { error: string }).error
        : `${res.status} ${res.statusText}`;
    throw new Error(msg);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const dispatch = (eventName: string, raw: string): void => {
    let payload: unknown;
    try {
      payload = JSON.parse(raw);
    } catch {
      return;
    }
    if (eventName === "meta") {
      onMessage({
        kind: "meta",
        data: payload as UploadDeckStreamMessage extends { kind: "meta"; data: infer D } ? D : never,
      });
    } else if (eventName === "done") {
      onMessage({ kind: "done", data: payload as UploadDeckDoneEvent });
    } else if (eventName === "error") {
      onMessage({ kind: "error", data: payload as { error: string } });
    } else if (eventName === "save_error") {
      onMessage({ kind: "save_error", data: payload as { error: string } });
    } else {
      onMessage({ kind: "event", data: payload as AgentEvent });
    }
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const rawEvent = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      let eventName = "message";
      const dataLines: string[] = [];
      for (const line of rawEvent.split("\n")) {
        if (line.startsWith("event:")) eventName = line.slice(6).trim();
        else if (line.startsWith("data:")) dataLines.push(line.slice(5).trimStart());
      }
      if (dataLines.length > 0) dispatch(eventName, dataLines.join("\n"));
    }
  }
}

export async function getDeck(id: string): Promise<SavedDeckDetail> {
  return jsonFetch(`${API_BASE}/api/decks/${encodeURIComponent(id)}`);
}

/**
 * Owner-first then public-fallback fetch for /decks/:slug.
 *
 * The route used to be owner-scoped only (404 for everything not
 * owned by the caller). Now /decks/<slug> can land on someone else's
 * public deck — first try the owned getter, and if that 404s, look up
 * the slug across all public decks. When multiple public matches
 * exist for the same slug (rare, but allowed), return the first one
 * (meta_reference rows sort to the top server-side) and surface the
 * full match list to the caller so the UI can render a chooser.
 */
export async function getDeckOrPublic(
  id: string,
): Promise<{
  detail: SavedDeckDetail;
  isOwner: boolean;
  otherMatches: PublicDeckMatch[];
}> {
  // Try owned first. 404 → fall through to the public path. The
  // status comes from the HTTP response itself rather than the
  // message string because the server's error envelope (`{ error
  // "deck not found: ..." }`) replaces the default status text in
  // the message, so a regex on the message never matched.
  try {
    const detail = await getDeck(id);
    return { detail, isOwner: true, otherMatches: [] };
  } catch (e) {
    const status = (e as Error & { status?: number }).status;
    if (status !== 404) throw e;
  }
  const matches = await findPublicDecksBySlug(id);
  if (matches.matches.length === 0) {
    throw new Error(`deck not found: ${id}`);
  }
  const first = matches.matches[0]!;
  const detail = await getPublicDeckByOwner(first.owner_username, first.slug);
  return {
    detail,
    isOwner: false,
    otherMatches: matches.matches.slice(1),
  };
}

export async function findPublicDecksBySlug(
  slug: string,
): Promise<{ count: number; matches: PublicDeckMatch[] }> {
  return jsonFetch(
    `${API_BASE}/api/decks/public/by-slug/${encodeURIComponent(slug)}`,
  );
}

export async function getPublicDeckByOwner(
  ownerUsername: string,
  slug: string,
): Promise<SavedDeckDetail> {
  return jsonFetch(
    `${API_BASE}/api/decks/public/${encodeURIComponent(ownerUsername)}/${encodeURIComponent(slug)}`,
  );
}

/**
 * Anonymous-friendly fetch for the new `/api/public/decks/:owner/:slug`
 * route family (PR2 / PUBLIC-DECKS plan). Used when the viewer is
 * NOT signed in — `getDeck`/`getDeckOrPublic` both attach a Bearer
 * token and would 401 with no Clerk session. This calls the
 * unauthenticated public route directly, which 404s for private decks
 * (indistinguishable from missing).
 *
 * Signed-in viewers should keep using `getDeckOrPublic` — that path
 * tries the owner-scoped endpoint first (so the owner sees their own
 * private decks too) and only falls back to the public lookup on 404.
 */
export async function getPublicDeckUnauth(
  ownerUsername: string,
  slug: string,
): Promise<SavedDeckDetail> {
  return jsonFetch(
    `${API_BASE}/api/public/decks/${encodeURIComponent(ownerUsername)}/${encodeURIComponent(slug)}`,
  );
}

/** Public-route counterpart of `listDeckAsks`. No auth required. */
export async function listPublicDeckAsks(
  ownerUsername: string,
  slug: string,
): Promise<{ deck_id: string; count: number; asks: DeckAsk[] }> {
  return jsonFetch(
    `${API_BASE}/api/public/decks/${encodeURIComponent(ownerUsername)}/${encodeURIComponent(slug)}/asks`,
  );
}

/** Public-route counterpart of `listDeckVersions` — read-only timeline. */
export async function listPublicDeckVersions(
  ownerUsername: string,
  slug: string,
): Promise<{ deck_id: string; count: number; versions: DeckVersionListItem[] }> {
  return jsonFetch(
    `${API_BASE}/api/public/decks/${encodeURIComponent(ownerUsername)}/${encodeURIComponent(slug)}/versions`,
  );
}

/** Public-route counterpart of `getDeckVersion` — full payload + diff
 *  for a single historical version. Same response shape as the authed
 *  endpoint so VersionTimeline can render either path. */
export async function getPublicDeckVersion(
  ownerUsername: string,
  slug: string,
  versionNo: number,
): Promise<DeckVersionDetail> {
  return jsonFetch(
    `${API_BASE}/api/public/decks/${encodeURIComponent(ownerUsername)}/${encodeURIComponent(slug)}/versions/${versionNo}`,
  );
}

export async function listPublicDecks(
  limit: number = 50,
  offset: number = 0,
): Promise<{ count: number; decks: SavedDeckSummary[] }> {
  return jsonFetch(
    `${API_BASE}/api/decks/public?limit=${limit}&offset=${offset}`,
  );
}

export async function setDeckVisibility(
  id: string,
  isPublic: boolean,
): Promise<{ id: string; is_public: boolean }> {
  return jsonFetch(`${API_BASE}/api/decks/${encodeURIComponent(id)}/visibility`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ is_public: isPublic }),
  });
}

/**
 * Fork a deck into the caller's account. `owner` is the source deck's
 * owner username; required when forking another user's public deck and
 * ignored (server resolves to caller) when forking your own. Returns
 * the new slug, which the caller uses to navigate to /decks/<me>/<slug>.
 */
export async function forkDeck(
  id: string,
  owner?: string,
): Promise<{ id: string; uuid: string; owner_username: string }> {
  return jsonFetch(`${API_BASE}/api/decks/${encodeURIComponent(id)}/fork`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(owner ? { owner } : {}),
  });
}

export async function deleteDeck(id: string): Promise<void> {
  await jsonFetch(`${API_BASE}/api/decks/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

// ─── Manual edits ──────────────────────────────────────────────────

/**
 * Server-side legality response. Mirrors `DeckValidation` from the
 * Node validator — kept in its own interface here so the web package
 * doesn't depend on the node-side types module.
 */
export interface DeckValidationResult {
  legal: boolean;
  errors: string[];
  warnings: string[];
  stats: {
    main_count: number;
    sideboard_count: number;
    color_identity: string[];
    mana_curve: Record<number, number>;
    color_source_counts: Record<string, number>;
    total_price_usd: number;
  };
}

export interface DeckUpdateBody {
  theme?: string;
  brief?: string;
  deck?: { mainboard: DeckEntry[]; sideboard: DeckEntry[] };
  /** Apply-Ask save: links the resulting version to the originating
   *  ask + charges upload_deck credits server-side. Only meaningful
   *  when `deck` is also present. */
  from_ask_id?: string;
  /** Mass-edit upload save: tells the server this deck change came
   *  from pasting a fresh Arena-format list, so the resulting version
   *  row is labeled source='upload'. Free at save. Mutually exclusive
   *  with `from_ask_id`. */
  from_upload?: boolean;
}

/** Raised when PUT /api/decks/:id returns 422 with a legality list. */
export class DeckEditValidationError extends Error {
  readonly errors: string[];
  constructor(errors: string[]) {
    super(errors[0] ?? "deck failed validation");
    this.name = "DeckEditValidationError";
    this.errors = errors;
  }
}

/** Apply a manual edit. Returns the post-update detail, ready to drop
 *  into the react-query cache for ["deck", id] and ["decks"]. */
export async function updateDeck(
  id: string,
  body: DeckUpdateBody,
): Promise<SavedDeckDetail> {
  const res = await fetch(`${API_BASE}/api/decks/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...(await authHeaders()) },
    body: JSON.stringify(body),
  });
  if (res.status === 422) {
    const payload = (await res.json()) as { errors?: unknown };
    if (Array.isArray(payload.errors)) {
      throw new DeckEditValidationError(payload.errors.map(String));
    }
    throw new Error("deck failed validation");
  }
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const j = await res.json();
      if (j?.error) msg = j.error;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return (await res.json()) as SavedDeckDetail;
}

/** Re-run the strategy prose against the deck's current card list.
 *  Charges ACTION_COSTS.regenerate_strategy server-side and writes a
 *  new deck_versions row with source='regenerate'. Returns the post-
 *  regen detail for drop-in cache replacement. Throws an Error tagged
 *  with `.status = 402` when the user is out of credits — callers can
 *  use `parseInsufficient` on a raw Response (use the gated-action
 *  helper) to drive the upgrade modal. */
export async function regenerateDeckStrategy(
  id: string,
): Promise<SavedDeckDetail> {
  const url = `${API_BASE}/api/decks/${encodeURIComponent(id)}/regenerate-strategy`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(await authHeaders()) },
  });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const j = await res.json();
      if (j?.error) msg = j.error;
    } catch {
      /* ignore */
    }
    const err = new Error(msg) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  return (await res.json()) as SavedDeckDetail;
}

/** Dry-run legality check against the server's authoritative card DB.
 *  Debounced in the editor so every keystroke doesn't round-trip. */
export async function validateDeckEdit(
  id: string,
  deck: { mainboard: DeckEntry[]; sideboard: DeckEntry[] },
): Promise<DeckValidationResult> {
  return jsonFetch(
    `${API_BASE}/api/decks/${encodeURIComponent(id)}/validate`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ deck }),
    },
  );
}

export async function getDeckTrace(
  id: string,
): Promise<{ id: string; events: AgentEvent[]; count: number }> {
  return jsonFetch(`${API_BASE}/api/decks/${encodeURIComponent(id)}/trace`);
}

export async function getPublicDeckTrace(
  ownerUsername: string,
  slug: string,
): Promise<{ id: string; events: AgentEvent[]; count: number }> {
  return jsonFetch(
    `${API_BASE}/api/public/decks/${encodeURIComponent(ownerUsername)}/${encodeURIComponent(slug)}/trace`,
  );
}

export interface DeckBuildSettings {
  exemplars_enabled?: boolean;
  progressive_discovery?: boolean;
  brief_category?:
    | "open"
    | "archetype"
    | "budget"
    | "mechanic"
    | "contrarian";
  exemplars_gated?: boolean;
  image_count?: number;
  image_extracted_text?: string;
}

export async function getDeckBuildSettings(
  id: string,
): Promise<{ id: string; settings: DeckBuildSettings }> {
  return jsonFetch(
    `${API_BASE}/api/decks/${encodeURIComponent(id)}/build-settings`,
  );
}

export interface DeckAsk {
  id: string;
  seq: number;
  question: string;
  answer: string;
  model: string;
  tokens_in: number | null;
  tokens_out: number | null;
  cost_usd: number | null;
  /** Ask-framing prompt version that produced this answer (e.g. "v3").
   *  Null on rows that pre-date the column. Surfaced inline on each
   *  ask card so the user can see which prompt iteration wrote it. */
  prompt_version: string | null;
  /** Conversation grouping (migration 0022). Every ask has a thread.
   *  Sibling asks share thread_id; root rows have parent_ask_id = null. */
  thread_id: string;
  parent_ask_id: string | null;
  thread_title: string | null;
  asked_at: string;
}

/** Aggregate row returned by GET /api/decks/:id/threads. One row per
 *  conversation, ordered by recency. */
export interface DeckThreadSummary {
  thread_id: string;
  title: string;
  message_count: number;
  first_question: string;
  last_ask_id: string;
  last_activity_at: string;
  root_ask_id: string;
}

export async function listDeckAsks(
  id: string,
): Promise<{ deck_id: string; count: number; asks: DeckAsk[] }> {
  return jsonFetch(`${API_BASE}/api/decks/${encodeURIComponent(id)}/asks`);
}

/** Conversation index for a deck. One row per thread, ordered by
 *  recency. Used by the threads-list UI in DeckDetail's Ask section. */
export async function listDeckThreads(
  id: string,
): Promise<{ deck_id: string; count: number; threads: DeckThreadSummary[] }> {
  return jsonFetch(
    `${API_BASE}/api/decks/${encodeURIComponent(id)}/threads`,
  );
}

/** Public-route counterpart. Anonymous-friendly; same shape. */
export async function listPublicDeckThreads(
  owner: string,
  slug: string,
): Promise<{ deck_id: string; count: number; threads: DeckThreadSummary[] }> {
  return jsonFetch(
    `${API_BASE}/api/public/decks/${encodeURIComponent(owner)}/${encodeURIComponent(slug)}/threads`,
  );
}

export interface MetaArchetype {
  archetype: string;
  top_color_identity: string;
  tournament_count: number;
  total_count: number;
  sample_source_urls: string[];
  max_tier_weight: number;
}

export interface MetaSnapshot {
  format: "standard";
  total_decks: number;
  tournament_decks: number;
  min_tier_threshold: number;
  /** Window over which decks were selected (days). null = all time. */
  window_days: number | null;
  archetypes: MetaArchetype[];
  sources: Array<{
    source: string;
    tier_weight: number;
    count: number;
    most_recent: string | null;
  }>;
  generated_at: string;
}

/** windowDays: positive integer (days), or "all" for no window. Defaults to server default (30d). */
export type MetaWindow = number | "all";

export async function getMeta(
  topN = 15,
  minTier = 1.3,
  windowDays: MetaWindow = 30,
): Promise<MetaSnapshot> {
  const params = new URLSearchParams({
    top_n: String(topN),
    min_tier: String(minTier),
    window_days: String(windowDays),
  });
  return jsonFetch<MetaSnapshot>(`${API_BASE}/api/meta?${params.toString()}`);
}

export async function scoreDeckById(deckId: string): Promise<{
  label: string;
  deck: Deck;
  score: ProximityScore;
}> {
  return jsonFetch(`${API_BASE}/api/score-deck`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ deck_id: deckId }),
  });
}

export async function getStats(): Promise<{
  cards: { count: number } | { error: string };
  decks: { count: number; by_source: Array<{ source: string; count: number; max_created_at: string | null }> } | { error: string };
  saved_decks: { count: number } | { error: string };
  files: { duckdb_exists: boolean; lance_exists: boolean };
}> {
  return jsonFetch(`${API_BASE}/api/stats`);
}

// ─── Playtest ─────────────────────────────────────────────────────────────

export interface PlaytestOutcome {
  deck_a_wins: number;
  deck_b_wins: number;
  draws: number;
  dnf: number;
  games_played: number;
}

export interface PlaytestJob {
  id: string;
  deck_a_id: string;
  deck_b_id: string;
  deck_a_theme: string;
  deck_b_theme: string;
  games: number;
  status: "running" | "complete" | "error";
  started_at: string;
  completed_at?: string;
  outcome: PlaytestOutcome;
  forge_version: string | null;
  error?: string;
}

export interface PlaytestProgressEvent {
  job_id: string;
  completed: number;
  total: number;
  outcome: PlaytestOutcome;
}

export interface PlaytestForgeMissingError {
  error: string;
  checked: string[];
  install_hint: string;
}

/**
 * Start a playtest. Rejects with a typed error on 409 (Forge missing) so
 * the UI can render install instructions inline instead of a toast.
 */
export async function startPlaytest(body: {
  deck_a_id: string;
  deck_b_id: string;
  games: number;
  /** Optional. When supplied and !== caller's username, the backend
   *  resolves that side via the public-decks path (deck must be
   *  is_public=true). Lets users pit an owned deck against a public
   *  one — typically a meta archetype — without copying it first. */
  deck_a_owner?: string;
  deck_b_owner?: string;
}): Promise<{ job_id: string }> {
  const res = await fetch(`${API_BASE}/api/playtest`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(await authHeaders()) },
    body: JSON.stringify(body),
  });
  if (res.status === 409) {
    const payload = (await res.json()) as PlaytestForgeMissingError;
    const err = new Error(payload.error) as Error & { forgeMissing: PlaytestForgeMissingError };
    err.forgeMissing = payload;
    throw err;
  }
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const j = (await res.json()) as { error?: string };
      if (j.error) msg = j.error;
    } catch {
      // ignore
    }
    throw new Error(msg);
  }
  return (await res.json()) as { job_id: string };
}

export async function getPlaytest(jobId: string): Promise<PlaytestJob> {
  return jsonFetch(`${API_BASE}/api/playtest/${encodeURIComponent(jobId)}`);
}

export async function getActivePlaytest(): Promise<{ active_job_id: string | null }> {
  return jsonFetch(`${API_BASE}/api/playtest/active`);
}

// ─── Match history (completed + errored jobs) ──────────────────────

export interface PlaytestHistoryJob extends PlaytestJob {
  /** Wall-clock duration from started_at to completed_at. Null while
   *  the job is still running (shouldn't appear in history). */
  elapsed_ms: number | null;
}

/** Compact history-row variant for a parent gauntlet run. The 3 child
 *  playtests are filtered out of the match list server-side, and a
 *  single gauntlet item appears in their place — clicking it deep-
 *  links to /mega-build/gauntlet/:id where the full per-archetype
 *  breakdown lives. */
export interface GauntletHistoryItem {
  id: string;
  deck_slug: string;
  deck_theme: string;
  deck_owner_username: string;
  status: "queued" | "running" | "summarizing" | "complete" | "error";
  total_games: number;
  user_wins: number;
  games_per_archetype: number;
  winrate: string | null;
  started_at: string;
  completed_at: string | null;
  elapsed_ms: number | null;
  error: string | null;
}

export type HistoryItem =
  | ({ kind: "match" } & PlaytestHistoryJob)
  | ({ kind: "gauntlet" } & GauntletHistoryItem);

export async function getPlaytestHistory(
  limit: number = 50,
  offset: number = 0,
): Promise<{ count: number; jobs: HistoryItem[] }> {
  const q = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  return jsonFetch(`${API_BASE}/api/playtest/history?${q}`);
}

export interface PlaytestGameSummary {
  game_index: number;
  winner: "a" | "b" | "draw" | "dnf" | null;
  duration_ms: number | null;
  log_bytes: number;
  created_at: string;
}

export async function getPlaytestGames(
  jobId: string,
): Promise<{ job_id: string; count: number; games: PlaytestGameSummary[] }> {
  return jsonFetch(
    `${API_BASE}/api/playtest/${encodeURIComponent(jobId)}/games`,
  );
}

export interface PlaytestGameLog {
  job_id: string;
  game_index: number;
  winner: "a" | "b" | "draw" | "dnf" | null;
  duration_ms: number | null;
  log: string;
  created_at: string;
}

export async function getPlaytestGameLog(
  jobId: string,
  gameIndex: number,
): Promise<PlaytestGameLog> {
  return jsonFetch(
    `${API_BASE}/api/playtest/${encodeURIComponent(jobId)}/games/${gameIndex}`,
  );
}

// ─── LLM match summary ─────────────────────────────────────────────

export interface PlaytestSummary {
  summary: string;
  prompt_version: string;
  model: string;
  tokens_in: number | null;
  tokens_out: number | null;
  created_at?: string;
  cached: boolean;
}

/**
 * Generate (or return cached) LLM recap of a completed playtest.
 * First call for a given (job, current prompt version) hits Bedrock;
 * subsequent calls return the persisted row.
 */
export async function summarizePlaytest(jobId: string): Promise<PlaytestSummary> {
  return jsonFetch(
    `${API_BASE}/api/playtest/${encodeURIComponent(jobId)}/summary`,
    { method: "POST" },
  );
}

// ─── Deck-scoped battles (recent jobs involving a specific deck) ───

export interface DeckBattle {
  id: string;
  deck_a_id: string;
  deck_a_theme: string;
  deck_b_id: string;
  deck_b_theme: string;
  games: number;
  status: "running" | "complete" | "error";
  started_at: string;
  completed_at: string | null;
  outcome: PlaytestOutcome;
  elapsed_ms: number | null;
}

export async function getDeckBattles(
  deckId: string,
): Promise<{ deck_id: string; count: number; jobs: DeckBattle[] }> {
  return jsonFetch(
    `${API_BASE}/api/decks/${encodeURIComponent(deckId)}/battles`,
  );
}

export type PlaytestStreamMessage =
  | { kind: "start"; data: PlaytestJob }
  | { kind: "progress"; data: PlaytestProgressEvent }
  | { kind: "done"; data: PlaytestJob }
  | { kind: "error"; data: { job_id: string; error: string } };

/**
 * Subscribe to a playtest job's SSE stream via EventSource. Returns a
 * disposer; callers should invoke it on unmount. The server replays
 * current state on connect, so refresh-tolerant.
 */
export function streamPlaytest(
  jobId: string,
  onMessage: (msg: PlaytestStreamMessage) => void,
): () => void {
  // EventSource can't set custom headers, so the backend auth middleware
  // also accepts a `?token=<jwt>` query param. Grab a fresh token before
  // opening the stream. If fetchAuthToken resolves to null (user signed
  // out), we still open the stream — the server will 401 and the UI
  // will render an error state, which is correct behavior.
  let es: EventSource | null = null;
  let disposed = false;
  const parse = (raw: string): unknown | null => {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };
  void (async () => {
    const token = await fetchAuthToken();
    if (disposed) return;
    const q = token ? `?token=${encodeURIComponent(token)}` : "";
    const url = `${API_BASE}/api/playtest/${encodeURIComponent(jobId)}/stream${q}`;
    const src = new EventSource(url);
    es = src;
    src.addEventListener("match_start", (e) => {
      const data = parse((e as MessageEvent).data);
      if (data) onMessage({ kind: "start", data: data as PlaytestJob });
    });
    src.addEventListener("match_progress", (e) => {
      const data = parse((e as MessageEvent).data);
      if (data) onMessage({ kind: "progress", data: data as PlaytestProgressEvent });
    });
    src.addEventListener("match_done", (e) => {
      const data = parse((e as MessageEvent).data);
      if (data) onMessage({ kind: "done", data: data as PlaytestJob });
      src.close();
    });
    src.addEventListener("error", (e) => {
      // Two kinds of "error" here: server-sent match failure events, and
      // EventSource transport errors. We get a MessageEvent with .data in
      // the former case, a plain Event in the latter.
      const me = e as MessageEvent;
      if (me.data) {
        const data = parse(me.data);
        if (data) onMessage({ kind: "error", data: data as { job_id: string; error: string } });
        src.close();
      }
      // Transport errors: let EventSource handle reconnect itself.
    });
  })();
  return () => {
    disposed = true;
    es?.close();
  };
}

export interface AskDoneEvent {
  answer: string;
  elapsed_ms: number;
  event_count: number;
  /** Server-assigned ids for the persisted ask row. Null in ephemeral
   *  mode (asking about someone else's public deck) or on a
   *  persistence failure — in either case the answer is still in the
   *  payload, but the client can't continue this ask in a thread. */
  ask_id: string | null;
  thread_id: string | null;
  parent_ask_id: string | null;
}

export type AskStreamMessage =
  | {
      kind: "meta";
      data: {
        deck_id: string;
        question: string;
        started_at: string;
        model: string;
        /** Ask-framing prompt version (e.g. "v3"). Lets the live ask
         *  card render `prompt vX` immediately, before the persisted
         *  row lands and `listDeckAsks` refetches. */
        ask_prompt_version?: string;
      };
    }
  | { kind: "event"; data: AgentEvent }
  | { kind: "done"; data: AskDoneEvent }
  | { kind: "error"; data: { error: string } };

/**
 * Stream an ask-about-deck run. Uses the same SSE-over-POST pattern as
 * streamBuildDeck (EventSource can't set POST bodies).
 */
export async function streamAskDeck(
  body: {
    deck_id: string;
    question: string;
    /** Card pool for this question; does not change the deck's source collection. */
    card_pool?: "collection" | "all";
    owner_username?: string;
    /** When set, the new ask is a reply continuing this thread.
     *  Server scopes the prompt context to the ancestor chain. */
    parent_ask_id?: string;
    /** Optional thread title — only honored on the first ask of a
     *  thread (i.e. when parent_ask_id is unset). Truncated to 200
     *  chars server-side. */
    thread_title?: string;
  },
  onMessage: (msg: AskStreamMessage) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/ask`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...(await authHeaders()),
    },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok || !res.body) {
    throw new Error(`ask failed: ${res.status} ${res.statusText}`);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const dispatch = (eventName: string, raw: string): void => {
    let payload: unknown;
    try {
      payload = JSON.parse(raw);
    } catch {
      return;
    }
    if (eventName === "meta") {
      onMessage({ kind: "meta", data: payload as AskStreamMessage extends { kind: "meta"; data: infer D } ? D : never });
    } else if (eventName === "done") {
      onMessage({ kind: "done", data: payload as AskDoneEvent });
    } else if (eventName === "error") {
      onMessage({ kind: "error", data: payload as { error: string } });
    } else {
      onMessage({ kind: "event", data: payload as AgentEvent });
    }
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const rawEvent = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      let eventName = "message";
      const dataLines: string[] = [];
      for (const line of rawEvent.split("\n")) {
        if (line.startsWith("event:")) eventName = line.slice(6).trim();
        else if (line.startsWith("data:")) dataLines.push(line.slice(5).trimStart());
      }
      if (dataLines.length > 0) dispatch(eventName, dataLines.join("\n"));
    }
  }
}

// ─── Billing / credits ─────────────────────────────────────────────

export interface CreditBalance {
  total: number;
  granted: number;
  purchased: number;
  plan_id: PlanId;
  grant_expires_at: string | null;
}

export interface BillingCatalogItem {
  /** Stable internal id — e.g. "pro", "topup-medium". */
  id: string;
  name: string;
  price_usd: number;
  credits: number;
  /** Polar product id used at checkout. May be empty string if Polar
   *  hasn't been provisioned yet (dev-only fallback). */
  polar_product_id: string;
}

export interface BillingCatalog {
  plans: BillingCatalogItem[];
  topups: BillingCatalogItem[];
}

/**
 * Shape of a 402 Payment Required response when an action is priced but
 * the user can't afford it. Backend sets this on any guarded route.
 */
export interface InsufficientCreditsError {
  error: "insufficient_credits";
  required: number;
  available: number;
  action: string;
}

export async function getBalance(): Promise<CreditBalance> {
  return jsonFetch<CreditBalance>(`${API_BASE}/api/billing/balance`);
}

export interface UserPlan {
  /** Polar status string ("active", "trialing", "canceled", etc.)
   *  or "none" when the user has never had a plan. */
  status: string;
  plan_id: string | null;
  /** Convenience: true when status is "active" or "trialing". The
   *  server collapses Polar's vocabulary so the UI doesn't have to
   *  track which states qualify. */
  is_subscriber: boolean;
}

export async function getMyPlan(): Promise<UserPlan> {
  return jsonFetch<UserPlan>(`${API_BASE}/api/billing/plan`);
}

export type DeckVersionSource =
  | "build"
  | "manual_edit"
  | "apply_ask"
  | "regenerate"
  | "revert"
  | "upload";

export interface DeckVersionListItem {
  version_no: number;
  source: DeckVersionSource;
  ask_id: string | null;
  prompt_version: string | null;
  /** When source='revert', the version_no this row restored from.
   *  Null on every other source. Lets the timeline strip render
   *  "v8 ← v5" without expanding the diff panel. */
  reverted_from_version_no: number | null;
  created_at: string;
}

export interface DeckDiff {
  added: Array<{ name: string; count: number; section: "main" | "side" }>;
  removed: Array<{ name: string; count: number; section: "main" | "side" }>;
  changed: Array<{
    name: string;
    section: "main" | "side";
    from: number;
    to: number;
  }>;
}

export interface DeckVersionDetail {
  deck_id: string;
  version_no: number;
  source: DeckVersionSource;
  ask_id: string | null;
  prompt_version: string | null;
  /** When source='revert', the version_no this row restored from.
   *  Null on every other source. */
  reverted_from_version_no: number | null;
  answer: string;
  parsed_deck: Deck | null;
  /** Diff against the previous version. Null for v1 (no predecessor)
   *  or when either version's answer fails to parse. */
  diff_from_previous: DeckDiff | null;
  previous_version_no: number | null;
  created_at: string;
}

export async function listDeckVersions(
  deckId: string,
): Promise<{ deck_id: string; count: number; versions: DeckVersionListItem[] }> {
  return jsonFetch(
    `${API_BASE}/api/decks/${encodeURIComponent(deckId)}/versions`,
  );
}

export async function getDeckVersion(
  deckId: string,
  versionNo: number,
): Promise<DeckVersionDetail> {
  return jsonFetch(
    `${API_BASE}/api/decks/${encodeURIComponent(deckId)}/versions/${versionNo}`,
  );
}

/** Restore a deck to a prior version's state by appending a new
 *  'revert' version. Server-side validates Standard legality of the
 *  target snapshot (rotation may have invalidated cards) — a 422 here
 *  carries the offending card list in `errors`. */
export async function revertDeckToVersion(
  deckId: string,
  versionNo: number,
): Promise<SavedDeckDetail> {
  return jsonFetch(
    `${API_BASE}/api/decks/${encodeURIComponent(deckId)}/versions/${versionNo}/revert`,
    { method: "POST" },
  );
}

export interface DeckEdit {
  kind: "remove" | "add" | "change_count";
  name: string;
  count: number;
  section?: "main" | "side";
  from?: number;
}

export interface ExtractedAskEdits {
  deck_id: string;
  ask_id: string;
  edits: DeckEdit[];
  unmapped: string;
  proposed_deck: Deck;
}

/** Run the Sonnet-backed extractor over an Ask answer. Returns the
 *  structured edit list plus a projected post-edit Deck the editor can
 *  pre-fill. Free — the actual upload-rate charge happens at save time. */
export async function extractAskEdits(
  deckId: string,
  askId: string,
): Promise<ExtractedAskEdits> {
  return jsonFetch(
    `${API_BASE}/api/decks/${encodeURIComponent(deckId)}/asks/${encodeURIComponent(askId)}/extract-edits`,
    { method: "POST" },
  );
}

export async function getBillingCatalog(): Promise<BillingCatalog> {
  return jsonFetch<BillingCatalog>(`${API_BASE}/api/billing/catalog`);
}

export async function createCheckout(
  product_id: string,
): Promise<{ url: string }> {
  return jsonFetch<{ url: string }>(`${API_BASE}/api/billing/checkout`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ product_id }),
  });
}

/**
 * Server returns `{ url }` pointing at Polar's customer portal. Caller
 * should `window.location.href = url` to navigate the top-level window.
 */
export async function openPortal(): Promise<string> {
  const { url } = await jsonFetch<{ url: string }>(`${API_BASE}/api/billing/portal`);
  return url;
}

/**
 * Attempt to parse a 402 from a thrown error. Our `jsonFetch` throws
 * `new Error(body.error)` on !ok responses, so for the 402 case we'd
 * get `Error("insufficient_credits")` but lose the numeric fields.
 * Rather than retrofit jsonFetch, callers that care about the details
 * should use `fetch` directly — this helper normalizes a raw Response.
 */
export async function parseInsufficient(
  res: Response,
): Promise<InsufficientCreditsError | null> {
  if (res.status !== 402) return null;
  try {
    const j = (await res.json()) as Partial<InsufficientCreditsError>;
    if (j.error === "insufficient_credits") {
      return {
        error: "insufficient_credits",
        required: Number(j.required ?? 0),
        available: Number(j.available ?? 0),
        action: String(j.action ?? ""),
      };
    }
  } catch {
    // malformed body — fall through
  }
  return null;
}

// ─── Collections ───────────────────────────────────────────────────

export type CollectionSource =
  | "manabox"
  | "moxfield"
  | "archidekt"
  | "deckbox"
  | "dragonshield"
  | "untapped"
  | "plain_text"
  | "manual";

export interface CollectionSummary {
  id: string;
  name: string;
  source: CollectionSource;
  card_count: number;
  oracle_count: number;
  imported_at: string;
  created_at: string;
  updated_at: string;
}

export interface CollectionDetail extends CollectionSummary {
  meta: Record<string, unknown>;
}

export interface CollectionEntry {
  oracle_id: string;
  quantity: number;
  foil_qty: number;
}

export interface CreateCollectionResult {
  id: string;
  source: CollectionSource;
  card_count: number;
  oracle_count: number;
  unresolved_count?: number;
  warnings?: string[];
}

export async function listCollections(): Promise<{ collections: CollectionSummary[] }> {
  return jsonFetch(`${API_BASE}/api/collections`);
}

export async function getCollection(id: string): Promise<CollectionDetail> {
  return jsonFetch(`${API_BASE}/api/collections/${encodeURIComponent(id)}`);
}

export async function getCollectionEntries(
  id: string,
): Promise<{ id: string; entries: CollectionEntry[] }> {
  return jsonFetch(
    `${API_BASE}/api/collections/${encodeURIComponent(id)}/entries`,
  );
}

export async function createCollectionFromCsv(
  csv: string,
  opts: { name?: string; source?: CollectionSource } = {},
): Promise<CreateCollectionResult> {
  return jsonFetch(`${API_BASE}/api/collections`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      csv,
      ...(opts.name ? { name: opts.name } : {}),
      ...(opts.source ? { source: opts.source } : {}),
    }),
  });
}

export async function deleteCollection(id: string): Promise<void> {
  await jsonFetch(`${API_BASE}/api/collections/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

// ─── Durable deck builds (PR 2 of DURABLE-BUILDS) ──────────────────

export interface BuildSummary {
  id: string;
  brief: string;
  status: "running" | "awaiting_input" | "complete" | "error" | "abandoned";
  started_at: string;
  completed_at: string | null;
  error: string | null;
  /** First line of the agent's question when status is awaiting_input,
   *  so the In-progress list can preview it next to the Resume link. */
  pending_question: string | null;
}

/** Kick off a new durable build. Returns the id to stream. */
export async function createBuild(body: {
  brief: string;
  exemplarMode?: ExemplarMode;
  save?: boolean;
  progressive_discovery?: boolean;
  /** Subscriber-only opt-in for Opus 4.7. Server silently downgrades
   *  for non-subscribers; UI hides the toggle in that case. */
  model_preference?: "sonnet" | "opus";
  /** Optional inspiration images. Server caps at 4 entries / 2MB each /
   *  jpeg|png|webp|gif. Vision pre-pass extracts MTG context, prepends
   *  to brief before the agent loop. */
  images?: Array<{ media_type: string; data_b64: string }>;
  /** When set, restrict the build to cards in this user-owned collection.
   *  Server loads oracle_ids + quantities from Postgres, threads them
   *  into runAgent({ collection }), and the validator enforces the
   *  arithmetic. */
  collection_id?: string;
}): Promise<{ build_id: string }> {
  return jsonFetch(`${API_BASE}/api/builds`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/** Submit the user's answer to a paused (awaiting_input) build. */
export async function answerBuild(
  buildId: string,
  answer: string,
): Promise<void> {
  const auth = await authHeaders();
  const res = await fetch(
    `${API_BASE}/api/builds/${encodeURIComponent(buildId)}/answer`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json", ...auth },
      body: JSON.stringify({ answer }),
    },
  );
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body?.error) msg = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
}

/** User-initiated abandon. Refunds the pre-charge server-side. */
export async function discardBuild(buildId: string): Promise<void> {
  const auth = await authHeaders();
  const res = await fetch(
    `${API_BASE}/api/builds/${encodeURIComponent(buildId)}/discard`,
    { method: "POST", headers: { ...auth } },
  );
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body?.error) msg = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
}

/** List the user's in-progress + errored builds for the resume UI. */
export async function listInProgressBuilds(): Promise<{
  count: number;
  builds: BuildSummary[];
}> {
  return jsonFetch(`${API_BASE}/api/builds`);
}

export interface DeckSavedEvent {
  saved_id: string | null;
  uuid: string | null;
  theme: string;
  elapsed_ms: number;
  model: string;
  prompt_version?: string;
  cost_estimate_usd?: number;
  token_usage?: Record<string, { input_tokens: number; output_tokens: number }>;
}

export type BuildStreamMessage =
  | { kind: "event"; data: AgentEvent; seq: number }
  | { kind: "deck_saved"; data: DeckSavedEvent; seq: number }
  | { kind: "run_error"; data: { error: string }; seq: number };

/**
 * Subscribe to a durable build via EventSource. The server replays all
 * events with seq > lastEventId, then tails new events until a
 * terminal event lands (deck_saved or run_error). EventSource handles
 * reconnect automatically using the Last-Event-ID header; we also pass
 * the query param for the first connection so POSTs don't need to
 * special-case it. Returns a disposer.
 */
export function streamBuild(
  buildId: string,
  onMessage: (msg: BuildStreamMessage) => void,
): () => void {
  let es: EventSource | null = null;
  let disposed = false;
  const parse = (raw: string): unknown | null => {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };
  void (async () => {
    const token = await fetchAuthToken();
    if (disposed) return;
    const q = token ? `?token=${encodeURIComponent(token)}` : "";
    const url = `${API_BASE}/api/builds/${encodeURIComponent(buildId)}/stream${q}`;
    const src = new EventSource(url);
    es = src;

    const dispatch = (kind: string, e: MessageEvent) => {
      const data = parse(e.data);
      if (data == null) return;
      const seq = Number.parseInt(e.lastEventId ?? "0", 10) || 0;
      if (kind === "deck_saved") {
        onMessage({ kind: "deck_saved", data: data as DeckSavedEvent, seq });
        src.close();
      } else if (kind === "run_error") {
        onMessage({ kind: "run_error", data: data as { error: string }, seq });
        src.close();
      } else {
        onMessage({ kind: "event", data: { type: kind, ...(data as object) } as AgentEvent, seq });
      }
    };

    const KINDS: Array<AgentEvent["type"] | "deck_saved" | "run_error"> = [
      "run_start",
      "classifier",
      "classifier_error",
      "turn_start",
      "turn_end",
      "assistant_text",
      "assistant_text_delta",
      "tool_call",
      "tool_result",
      "final_answer",
      "usage",
      "awaiting_input",
      "user_response",
      "error",
      "deck_saved",
      "run_error",
    ];
    for (const k of KINDS) {
      src.addEventListener(k, (e) => dispatch(k, e as MessageEvent));
    }
  })();
  return () => {
    disposed = true;
    es?.close();
  };
}

// ─── mega-build ─────────────────────────────────────────────────────

export interface MegaBuildJob {
  id: string;
  user_id: string;
  brief: string;
  status: "running" | "complete" | "error";
  phase:
    | "build"
    | "gauntlet_v1"
    | "edit"
    | "gauntlet_v2"
    | "present"
    | "done"
    | "error";
  initial_build_id: string | null;
  gauntlet_v1_id: string | null;
  edit_build_id: string | null;
  gauntlet_v2_id: string | null;
  final_deck_id: string | null;
  credit_ledger_id: number | null;
  error: string | null;
  started_at: string;
  completed_at: string | null;
}

export async function createMegaBuild(brief: string): Promise<{ mega_id: string }> {
  return jsonFetch(`${API_BASE}/api/mega-build`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ brief }),
  });
}

export async function getMegaBuild(megaId: string): Promise<MegaBuildJob> {
  return jsonFetch(`${API_BASE}/api/mega-build/${encodeURIComponent(megaId)}`);
}

export async function listMegaBuilds(
  status: "running" | "complete" | "error" | "all" = "running",
): Promise<{ count: number; jobs: MegaBuildJob[] }> {
  return jsonFetch(`${API_BASE}/api/mega-build?status=${status}`);
}

export interface MegaBuildEvent {
  seq: number;
  kind: string;
  payload: unknown;
  occurred_at: string;
}

export interface MegaProgress {
  mega: MegaBuildJob;
  active_build_events: MegaBuildEvent[] | null;
  active_gauntlet: GauntletJob | null;
}

export async function getMegaProgress(megaId: string): Promise<MegaProgress> {
  return jsonFetch(
    `${API_BASE}/api/mega-build/${encodeURIComponent(megaId)}/progress`,
  );
}

export type MegaStreamMessage =
  | { kind: "update"; data: MegaBuildJob }
  | { kind: "done"; data: MegaBuildJob }
  | { kind: "error"; data: { error: string } };

export function streamMegaBuild(
  megaId: string,
  onMessage: (msg: MegaStreamMessage) => void,
): () => void {
  let es: EventSource | null = null;
  let disposed = false;
  const parse = (raw: string): unknown | null => {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };
  void (async () => {
    const token = await fetchAuthToken();
    if (disposed) return;
    const q = token ? `?token=${encodeURIComponent(token)}` : "";
    const url = `${API_BASE}/api/mega-build/${encodeURIComponent(megaId)}/stream${q}`;
    const src = new EventSource(url);
    es = src;
    src.addEventListener("mega_update", (e) => {
      const data = parse((e as MessageEvent).data);
      if (data) onMessage({ kind: "update", data: data as MegaBuildJob });
    });
    src.addEventListener("mega_done", (e) => {
      const data = parse((e as MessageEvent).data);
      if (data) onMessage({ kind: "done", data: data as MegaBuildJob });
      src.close();
    });
    src.addEventListener("error", (e) => {
      const me = e as MessageEvent;
      if (me.data) {
        const data = parse(me.data);
        if (data) onMessage({ kind: "error", data: data as { error: string } });
        src.close();
      }
      // Transport errors: let EventSource auto-reconnect.
    });
  })();
  return () => {
    disposed = true;
    es?.close();
  };
}

// ─── gauntlet ───────────────────────────────────────────────────────

export interface GauntletArchetypeOutcome {
  archetype_deck_id: string;
  archetype_slug: string;
  archetype_theme: string;
  playtest_job_id: string | null;
  user_wins: number;
  archetype_wins: number;
  draws: number;
  dnf: number;
  games_played: number;
}

export interface GauntletJob {
  id: string;
  user_id: string;
  user_deck_id: string;
  /** Slug of the user-side deck. Joined server-side from `decks` so
   *  the gauntlet detail page can deep-link back to /decks/:owner/:slug
   *  without an extra round-trip. Null when the deck has been deleted
   *  since the gauntlet ran. */
  user_deck_slug: string | null;
  user_deck_theme: string | null;
  user_deck_owner_username: string | null;
  archetype_deck_ids: string[];
  child_playtest_ids: string[];
  games_per_archetype: number;
  status: "queued" | "running" | "summarizing" | "complete" | "error";
  phase: string | null;
  outcome: {
    per_archetype: GauntletArchetypeOutcome[];
    total_games: number;
    total_user_wins: number;
  };
  rollup_summary: string | null;
  rollup_summary_json: Record<string, unknown> | null;
  winrate: string | null;
  error: string | null;
  started_at: string;
  completed_at: string | null;
}

export async function getGauntlet(gauntletId: string): Promise<GauntletJob> {
  return jsonFetch(`${API_BASE}/api/gauntlet/${encodeURIComponent(gauntletId)}`);
}

/** Compact gauntlet row used by DeckDetail's "past gauntlets" list. */
export interface GauntletListItem {
  id: string;
  status: "queued" | "running" | "summarizing" | "complete" | "error";
  phase: string | null;
  winrate: string | null;
  total_games: number;
  total_user_wins: number;
  games_per_archetype: number;
  started_at: string;
  completed_at: string | null;
  error: string | null;
}

/** GET /api/gauntlet?deck_id=<slug> — list recent gauntlets for a
 *  user-owned deck. Returns empty array (not an error) when the deck
 *  has never been gauntlet'd. */
export async function listGauntletsForDeck(
  deckSlug: string,
): Promise<{ deck_id: string; count: number; gauntlets: GauntletListItem[] }> {
  return jsonFetch(
    `${API_BASE}/api/gauntlet?deck_id=${encodeURIComponent(deckSlug)}`,
  );
}

/** POST /api/gauntlet/:id/summarize — generate the editor's brief
 *  retroactively. Charges ACTION_COSTS.gauntlet_summary on cache miss;
 *  cache hit returns the existing brief with no charge. */
export async function summarizeGauntlet(
  gauntletId: string,
): Promise<{
  gauntlet_id: string;
  cached: boolean;
  rollup_summary: string | null;
  rollup_summary_json: Record<string, unknown> | null;
  winrate: string | null;
}> {
  return jsonFetch(
    `${API_BASE}/api/gauntlet/${encodeURIComponent(gauntletId)}/summarize`,
    { method: "POST" },
  );
}

/** POST /api/gauntlet — start a fresh gauntlet for one of the caller's
 *  own decks. Returns the new gauntlet UUID for routing to
 *  /mega-build/gauntlet/:id. The server pre-charges credits and 429s
 *  if any other playtest is in flight; callers should surface the 429
 *  message verbatim.
 *
 *  `archetypeDeckIds` — optional override of the meta_reference roster
 *  (1–5 deck UUIDs). Server defaults to the 3 currently-active
 *  archetypes when omitted. Used by the "Change Opponents" UI to mix
 *  in historical meta archetypes.
 */
export async function startGauntlet(
  userDeckSlug: string,
  gamesPerArchetype = 10,
  archetypeDeckIds?: string[],
): Promise<{ gauntlet_id: string }> {
  return jsonFetch(`${API_BASE}/api/gauntlet`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      user_deck_id: userDeckSlug,
      games_per_archetype: gamesPerArchetype,
      ...(archetypeDeckIds && archetypeDeckIds.length > 0
        ? { archetype_deck_ids: archetypeDeckIds }
        : {}),
    }),
  });
}

/** GET /api/gauntlet/archetypes — list meta_reference decks (active +
 *  historical) for the gauntlet's "Change Opponents" UI. */
export interface GauntletArchetype {
  id: string;
  slug: string;
  theme: string;
  archetype_guess: string | null;
  is_active: boolean;
  created_at: string;
}
export async function listGauntletArchetypes(): Promise<{
  archetypes: GauntletArchetype[];
}> {
  return jsonFetch(`${API_BASE}/api/gauntlet/archetypes`);
}

/** Payment verification returns only the fields needed for conversion reporting. */
export async function getCheckoutPurchase(checkoutId: string): Promise<{
  purchase: import("../../../src/billing/checkout-purchase").VerifiedCheckoutPurchase | null;
}> {
  return jsonFetch(`${API_BASE}/api/billing/success?checkout_id=${encodeURIComponent(checkoutId)}`);
}
